"use client";

import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Loader, ArrowRight, Sparkles, Leaf, SprayCan } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
function JoinForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-8 rounded-lg border-[1px] border-platinum-border bg-alabaster-surface">
        <div className="flex items-center gap-3 mb-3">
          <Sparkles className="size-6 text-primary" />
          <p className="font-default text-2xl text-secondary font-semibold">Request Received</p>
        </div>
        <p className="text-slate-muted mb-6">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="border-[1px] border-secondary bg-background text-secondary rounded-md px-6 py-3 font-default font-bold hover:bg-secondary hover:text-background transition-colors">
          Submit another inquiry
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-5">
      <FormField control={control} name="full_name" render={({
      field
    }) => <FormItem>
            <FormLabel className="font-mono text-xs tracking-widest uppercase text-secondary">
              Full Name
            </FormLabel>
            <FormControl>
              <Input placeholder="John Alexander" {...field} className="bg-transparent border-b-[1px] border-t-0 border-x-0 border-platinum-border h-12 px-0 text-secondary placeholder:text-slate-muted/40 focus-visible:ring-0 focus-visible:border-primary transition-all" />
            </FormControl>
            <FormDescription className="text-slate-muted text-xs">
              As it appears on your property deed or contract.
            </FormDescription>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="email" rules={{
      required: "Email is required",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Please enter a valid email"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-mono text-xs tracking-widest uppercase text-secondary">
              Corporate Email
            </FormLabel>
            <FormControl>
              <Input type="email" placeholder="alexander@elite.com" {...field} className="bg-transparent border-b-[1px] border-t-0 border-x-0 border-platinum-border h-12 px-0 text-secondary placeholder:text-slate-muted/40 focus-visible:ring-0 focus-visible:border-primary transition-all" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="zip_code" render={({
      field
    }) => <FormItem>
            <FormLabel className="font-mono text-xs tracking-widest uppercase text-secondary">
              Property Location
            </FormLabel>
            <FormControl>
              <Input placeholder="Los Cabos, BCS" {...field} className="bg-transparent border-b-[1px] border-t-0 border-x-0 border-platinum-border h-12 px-0 text-secondary placeholder:text-slate-muted/40 focus-visible:ring-0 focus-visible:border-primary transition-all" />
            </FormControl>
            <FormDescription className="text-slate-muted text-xs">
              Specify the resort or residential development.
            </FormDescription>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 border-[1px] border-destructive rounded-md bg-destructive/10">
          <p className="text-destructive font-default font-medium text-sm">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-14 bg-primary text-primary-foreground border-none font-default font-bold text-base hover:bg-secondary hover:text-background transition-all duration-200 uppercase tracking-wider">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-4" />
            Processing...
          </> : <>
            Request Assessment
            <ArrowRight className="ml-1 size-5" />
          </>}
      </Button>
    </Form>;
}
export default function PartnerWithUs() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const badges = [{
    text: "PROFECO COMPLIANT",
    bg: "bg-secondary text-background",
    rotate: "rotate-0"
  }, {
    text: "SAT REGISTERED",
    bg: "bg-primary text-primary-foreground",
    rotate: "rotate-0"
  }, {
    text: "29 YRS EXPERTISE",
    bg: "bg-muted text-secondary",
    rotate: "rotate-0"
  }];
  return <section data-section-id="1719" id="partner-with-us" className="relative bg-background text-foreground py-28 md:py-40 overflow-hidden border-t-[1px] border-platinum-border">
      {}
      <div className="absolute top-0 left-0 right-0 h-1 bg-primary" aria-hidden="true" />

      <div className="max-w-[1360px] mx-auto px-8 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {}
          <div ref={leftRef} data-index={0} className={`lg:col-span-5 transition-all duration-700 ease-out ${leftVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            {}
            <div className="flex items-center gap-3 mb-8">
              <span className="font-mono text-xs md:text-sm font-semibold tracking-widest uppercase text-primary">
                06 / PARTNER WITH US
              </span>
              <span className="h-px bg-platinum-border w-20" aria-hidden="true" />
            </div>

            {}
            <h2 className="font-serif text-5xl sm:text-6xl md:text-7xl lg:text-[5.5rem] leading-[0.95] tracking-tighter text-secondary mb-8">
              Secure your{" "}
              <span className="relative inline-block">
                <span className="absolute inset-0 bg-primary/10 -z-0" style={{
                top: "60%",
                bottom: "5%",
                left: "-2%",
                right: "-2%"
              }} aria-hidden="true" />
                <span className="relative z-10 text-secondary">legacy</span>
              </span>
              .
            </h2>

            <p className="text-lg md:text-xl text-slate-muted max-w-[420px] leading-relaxed mb-10 font-default">
              Join the elite network of property owners in Los Cabos. From asset liberation to luxury management, we architect your success.
            </p>

            {}
            <div className="flex flex-col items-start gap-4 mb-10">
              {badges.map((badge, i) => <div key={i} data-index={i} className={`${badge.bg} ${badge.rotate} border-[1px] border-platinum-border rounded-none px-4 py-2 font-mono text-xs md:text-sm tracking-widest shadow-sm`}>
                  {badge.text}
                </div>)}
            </div>

            {}
            <div className="relative h-32 -ml-4 hidden md:block opacity-20" aria-hidden="true">
              <Sparkles className="absolute left-2 top-2 size-14 text-secondary" strokeWidth={1.5} />
              <div className="absolute left-24 top-8 border-[1px] border-secondary p-2">
                <ArrowRight className="size-10 text-primary rotate-45" strokeWidth={1.5} />
              </div>
              <Sparkles className="absolute left-44 top-0 size-10 text-secondary" strokeWidth={1.5} />
            </div>
          </div>

          {}
          <div ref={rightRef} data-index={1} className={`lg:col-span-7 transition-all duration-700 ease-out delay-150 ${rightVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <div className="relative">
              {}
              <div className="absolute -top-4 right-8 md:right-16 w-32 h-1 bg-primary z-20" aria-hidden="true" />

              {}
              <div className="relative bg-alabaster-surface border-[1px] border-platinum-border p-8 md:p-10 shadow-lg">
                <div className="mb-8">
                  <p className="font-mono text-xs tracking-widest uppercase text-primary mb-3">
                    REFERRAL & PARTNERSHIP
                  </p>
                  <h3 className="font-serif text-3xl md:text-4xl text-secondary leading-tight">
                    Begin your consultation
                  </h3>
                </div>

                <FormProvider formKey="partner_form_contact" formId="partner_form_contact" sectionName="partner-with-us" defaultValues={{
                full_name: "",
                email: "",
                zip_code: ""
              }} validationRules={{
                full_name: {
                  required: true
                },
                email: {
                  required: true,
                  pattern: "email"
                },
                zip_code: {
                  required: false
                }
              }} submitText="Request Assessment" successMessage="Your inquiry has been received. An IMI Elite specialist will contact you within 24 business hours." errorMessage="Transmission error. Please contact us directly at imielitecabo@gmail.com.">
                  <JoinForm />
                </FormProvider>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}